import SuperRecordActions from "data/SuperRecordActions";
import withOver, { appAlert } from "lib/withOver";
import Dispatcher from "Dispatcher";
import safeContextDispatch from "data/util/safeContextDispatch";
import safeDispatch from "data/util/safeDispatch";
import NavigationActions from "data/NavigationActions";
import RecordActions, { OPT_USER_CONFIRMED } from "data/RecordActions";
import SuperRecordStore from "data/SuperRecordStore";
import ClientId from "data/ClientId";
import {
    getSuperSelectedVersion,
    rerouteFieldNav,
    superNavTree,
    TOP_LEVEL_RECORDS_KEY,
} from "data/selectors/superRecords";
import userStore from "data/userStore";
import entityFieldMetaStore from "data/meta/entityFieldMetaStore";
import NavigatorStore from "data/NavigatorStore";
import TableStore from "data/TableStore";
import { saveSuperRecord } from "data/util/saveit";
import EntityName from "data/util/EntityName";
import OverActions from "data/OverActions";
import { ACTION_CONTEXT_KEY } from "data/util/ContextedReduceStore";
import hasUnsavedChanges from "data/selectors/hasUnsavedChanges";
import DataManager from "data/DataManager";
import contextFromAppModelOrContext from "./util/contextFromAppModelOrContext";

const findDeepestItem = (tree) => {
    let maxDepth = -1;
    let deepestItem = null;
    const dig = (item, depth = 0) => {
        if (ClientId.is(item.id)) return; // can't be down this way
        if(item.isMagic) {
            // find the selected version in projections
            const v = getSuperSelectedVersion(item);
            if(v) {
                maxDepth = depth;
                deepestItem = {id: item.id, entityKey: v.key};
            }
        }
        else if (item.selected) {
            if (depth > maxDepth) {
                maxDepth = depth;
                deepestItem = item;
            }
        }
        if (item.fields == null) return;
        for (const f of item.fields) {
            if (f.items == null) continue;
            for (const it of f.items) {
                dig(it, depth + 1);
            }
        }
    };
    dig(tree);
    return deepestItem;
};

const noop = () => {
    /* noop */
};

class DataEntryControllerJS extends Backbone.Controller {

    constructor() {
        super();
        Dispatcher.register(action => {
            // bridge from Flux back to Backbone.Controller-ish, but leave a
            // tick since many things come out of BB.C dispatch.
            switch(action.type) {
                case SuperRecordActions.CANCEL:
                    // eslint-disable-next-line no-settimeout
                    setTimeout(() =>
                        this.onConfirmCancelEntity(action[ACTION_CONTEXT_KEY]));
                    break;
                case SuperRecordActions.CANCEL_FOR_OPTLOCK:
                    // we need to waitFor TableStore here, but the setTimeout will
                    // do it for us for free.
                    // eslint-disable-next-line no-settimeout
                    setTimeout(() =>
                        this.onCancelEntity(action[ACTION_CONTEXT_KEY]));
                    break;
                case SuperRecordActions.SAVE:
                    this.onSaveEntity(action[ACTION_CONTEXT_KEY]);
                    break;
                default:
            }
        });
    }

    _promiseSaveConfirmation(appModelOrFluxContext) {
        const fluxContext = contextFromAppModelOrContext(appModelOrFluxContext);
        if (hasUnsavedChanges(fluxContext)) {
            return new Promise((resolve, reject) => {
                const yes_action = {label: "Yes", callback: () => {
                        const token = Dispatcher.register(action => {
                            // eslint-disable-next-line default-case
                            switch (action.type) {
                                case SuperRecordActions.CREATED:
                                case SuperRecordActions.UPDATED:
                                    Dispatcher.unregister(token);
                                    return resolve(true);
                                case SuperRecordActions.CREATE_ERROR:
                                case SuperRecordActions.UPDATE_ERROR:
                                    Dispatcher.unregister(token);
                                    return reject();
                            }
                        });
                        this.dispatch("entity:save", fluxContext);
                    }};
                const no_action = {label: "No", callback: () => resolve(false)};
                const cancel_action = {label: "Cancel", callback: reject};
                appAlert({
                    message: "Would you like to save your changes?",
                    actions: [cancel_action, no_action, yes_action],
                    spinner: false
                });
            });
        } else {
            return Promise.resolve();
        }
    }

    _promiseCancelConfirmation(appModelOrFluxContext) {
        const fluxContext = contextFromAppModelOrContext(appModelOrFluxContext);
        if (hasUnsavedChanges(fluxContext)) {
            return new Promise((resolve, reject) => {
                const yes_action = {label: "Yes", callback: resolve};
                const no_action = {label: "No", callback: reject};
                appAlert({
                    message: "Are you sure you would like to cancel your changes?",
                    actions: [no_action, yes_action],
                    spinner: false
                });
            });
        } else {
            return Promise.resolve();
        }
    }

    onConfirmUserLogout(appModel) {
        this._promiseSaveConfirmation(appModel)
            .then(
                () => Dispatcher.dispatch({
                    type: OverActions.EXTERNAL_NAV,
                    [OPT_USER_CONFIRMED]: true,
                    message: "Logging out...",
                    // eslint-disable-next-line no-settimeout
                    callback: () => setTimeout(() =>
                        this.dispatch("user:logout")),
                }),
                noop);
    }

    onConfirmLoadEntity(searchResult, appModel) {
        this._promiseSaveConfirmation(appModel)
            .then(
                () => this.onLoadEntity(searchResult, appModel),
                noop);
    }

    onConfirmEntityNew(key, org, entity, appModel) {
        this._promiseSaveConfirmation(appModel)
            .then(
                () => this.onEntityNew(key, org, entity, appModel),
                noop);
    }

    onConfirmCancelEntity(appModelOrFluxContext) {
        const fluxContext = contextFromAppModelOrContext(appModelOrFluxContext);
        this._promiseCancelConfirmation(fluxContext).then(
            () => this.onCancelEntity(fluxContext),
            noop);
    }

    onCancelEntity(fluxContext) {
        const magic = NavigatorStore.getMagicState(fluxContext);
        if (!magic) return;
        if (ClientId.is(magic.id)) {
            Dispatcher.contextDispatch(fluxContext, {
                type: SuperRecordActions.DELETE_RECORD,
                key: magic.key,
                id: magic.id,
                [OPT_USER_CONFIRMED]: true,
            });
        } else {
            this.getDeepestPersisted(fluxContext);
        }
    }

    getDeepestPersisted(appModelOrFluxContext) {
        const fluxContext = contextFromAppModelOrContext(appModelOrFluxContext);
        const navState = NavigatorStore.getState(fluxContext);
        const {magic} = navState;
        const tree = superNavTree(
            TableStore,
            SuperRecordStore.getRecord(
                magic.key,
                magic.id,
            ),
            navState,
            magic.key,
            magic.id,
        );

        const deepestItem = findDeepestItem(tree);
        const org = userStore.getActiveOrgKey();
        let ekey = undefined;
        let eid = undefined;
        if (deepestItem != null) {
            // if we're synthesizing a stereotype, still use that key
            ekey = deepestItem.stereoKey && EntityName.is(deepestItem.stereoKey)
                ? deepestItem.stereoKey
                : deepestItem.entityKey;
            eid = deepestItem.id;
        }
        this.dispatch("load:entity_data", org, magic.key, magic.id, ekey, eid, undefined, fluxContext);
    }

    onEntityNew(key, org, stereoKey, appModel) {
        this.dispatch("user:activity");
        withOver("Creating new record...", () =>
            DataManager.promiseModelAwareness(org, stereoKey)
                .then(() => {
                    Dispatcher.contextDispatch(appModel.get("fluxContext"), {
                        type: SuperRecordActions.NEW,
                        stereoKey,
                        recordKey: key,
                        newId: ClientId.next(),
                        navigate: true,
                        [OPT_USER_CONFIRMED]: true,
                    });
                })
        );
    }

    onConfirmDataEntry(org, key, id, ekey, eid, shortname, appModel) {
        this._promiseSaveConfirmation(appModel)
            .then(
                () => this.onDataEntry(org, key, id, ekey, eid, shortname, appModel),
                noop);
    }

    onDataEntry(org, key, id, ekey, eid, field, appModelOrFluxContext) {
        const fluxContext = contextFromAppModelOrContext(appModelOrFluxContext);
        if (userStore.getActiveOrgKey() !== org) {
            return;
        }
        return withOver("Loading...", () =>
            //todo: these two loads (model and data) should be parallel, but
            // they race, and it's easier to serialize rather than lock.
            DataManager.promiseModelAwareness(org, key)
                .then(() =>
                    SuperRecordStore.getRecordLO(key, id, true).promise())
                .then(() => {
                    if (ekey && eid && field) {
                        [ekey, eid, field] = this.rerouteFieldNav({key, id}, ekey, eid, field);
                    }
                    Dispatcher.contextDispatch(fluxContext, {
                        type: SuperRecordActions.OPEN,
                        key,
                        id,
                        entityKey: ekey || null,
                        recordId: eid || null,
                        fieldKey: field || null,
                    });
                })
                .catch(jqXHR => {
                    if (jqXHR.status < 500) return;
                    console.error(`error loading record ${key}#${id}`, jqXHR);
                    let msg = "Unable to load record";
                    if (jqXHR.responseText !== "") {
                        //xhr.responseText = '{"message":"Record 2138743 does not exist in the database for stereotype patient","type":"RecordNotFoundException","id":2138743,"key":null}'
                        try {
                            const resp = JSON.parse(jqXHR.responseText);
                            msg = resp.message;
                        } catch (error) {/* noop */}
                    }
                    appAlert(msg);
                }));
    }

    onSaveEntity(appModelOrFluxContext) {
        const fluxContext = contextFromAppModelOrContext(appModelOrFluxContext);
        const sr = NavigatorStore.getMagicState(fluxContext);
        saveSuperRecord(sr.key, sr.id, fluxContext);
        SuperRecordStore.getRecordLO(sr.key, sr.id).promise();
    }

    // "topLevelRecords" is a magic `fieldKey` indicating record is a magic root
    // and the new child is a magic child.
    onRecordNewChild(record, fieldKey, entityKey, options = {}, appModelOrFluxContext) {
        const fluxContext = contextFromAppModelOrContext(appModelOrFluxContext);
        const isMagic = fieldKey === TOP_LEVEL_RECORDS_KEY;
        const newId = ClientId.next();
        if (isMagic) {
            safeDispatch({
                type: SuperRecordActions.NEW_MAGIC_CHILD,
                recordKey: record.entityKey,
                recordId: record.id,
                entityKey,
                newId,
                // navigate: true, // todo: shouldn't this be here?
            });
        } else {
            // this double-dispatch is kludge-y, but the stores are still too
            // "inverted" to do correctly at the moment
            const navigate = entityFieldMetaStore.getInfo(record.entityKey, fieldKey).navigable;
            safeContextDispatch(fluxContext, {
                type: RecordActions.NEW_CHILD,
                recordKey: record.entityKey,
                recordId: record.id,
                fieldKey,
                entityKey,
                newId,
                focusFieldKey: options.shortname,
                insertAt: options.at,
                navigate,
            });
        }
    }

    // this is where:
    //  onRecordNewChild
    //  onValidationLoadEntity
    //  onActiveRecordChange
    //  onDataEntry (maybe?)
    // will refocus
    onRecordOpen(entityKey, recordId, fieldKey, appModelOrFluxContext) {
        const fluxContext = contextFromAppModelOrContext(appModelOrFluxContext);
        safeContextDispatch(fluxContext, {
            type: NavigationActions.SELECT_RECORD,
            entityKey,
            recordId,
            fieldKey,
        });
    }

    rerouteFieldNav(magic, entityKey, recordId, fieldKey) {
        return rerouteFieldNav(magic, entityKey, recordId, fieldKey);
    }

    onValidationLoadEntity(key, id, field, appModelOrFluxContext) {
        // it's be awesome to combine this w/ onChildNew, but
        // there's still to much navigation/focus collusion
        const fluxContext = contextFromAppModelOrContext(appModelOrFluxContext);
        const magic = NavigatorStore.getMagicState(fluxContext);
        [key, id, field] = this.rerouteFieldNav(magic, key, id, field);
        this.onRecordOpen(key, id, field, appModelOrFluxContext);
    }

}

export default DataEntryControllerJS;



// WEBPACK FOOTER //
// ./app/controllers/DataEntryControllerJS.js